import React, { useLayoutEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getCurrentUser, fetchAuthSession } from '@aws-amplify/auth';
import {jwtDecode} from 'jwt-decode';
import RoutesBeforeLogin from './routesWithoutLogin/Router';
import MenuLayout from '../layout/menu/MenuLayout';
import { AuthGateSkeleton } from '../pages/whatsapp/components/skeletons/WhatsAppPageSkeletons';
import { clearUserDetail, setAuthLoading, setUserDetail } from '../store/authSlice';
import { updateUserInRedux } from '../utils/updateUserInRedux';
import { dispatchAccountSetupAndWait } from '../utils/accountSetupHelper';
import { pollTokenRefresh } from '../utils/tokenRefreshPoller';
import { getAuthFromStorage, saveAuthToStorage, clearAuthFromStorage } from '../utils/authStorageHelper';
import { isTokenExpired } from '../utils/tokenHelpers';
import { fetchCapabilitiesRequest } from '../store/capabilitiesSlice';
import { clearCapabilitiesFromStorage } from '../utils/capabilitiesStorage';
import OptionalBusinessDetailsDialog from '../components/auth/whatsapp/OptionalBusinessDetailsDialog';

const Routes = () => {
    const dispatch = useDispatch();
    const store = useStore();
    const isUserAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const authUser = useSelector((state) => state.auth.user);
    const [dismissedBusinessDialog, setDismissedBusinessDialog] = useState(false);
    const shouldPromptForBusinessDetails = isUserAuthenticated && !dismissedBusinessDialog && (
        !authUser?.phoneNumber || authUser?.phoneNumberVerified !== true
    );

    const checkAuthSession = async () => {
        try {
            const cachedAuth = getAuthFromStorage();
            if (cachedAuth) {
                const cachedIdTokenPayload = cachedAuth.tokens?.idToken ? jwtDecode(cachedAuth.tokens.idToken) : null;
                const normalizedCachedAuth = {
                    ...cachedAuth,
                    phoneNumberVerified:
                        cachedAuth.phoneNumberVerified ?? cachedIdTokenPayload?.phone_number_verified,
                };

                if (isTokenExpired(cachedAuth.tokens?.idToken)) {
                    console.warn('Cached token is expired, refreshing from Cognito...');
                    try {
                        const { tokens } = await fetchAuthSession({ forceRefresh: true });
                        const refreshedIdTokenPayload = tokens?.idToken ? jwtDecode(tokens.idToken.toString()) : null;
                        const refreshedAuth = {
                            ...cachedAuth,
                            tokens: {
                                accessToken: tokens?.accessToken?.toString(),
                                idToken: tokens?.idToken?.toString(),
                            },
                            phoneNumberVerified:
                                cachedAuth.phoneNumberVerified ?? refreshedIdTokenPayload?.phone_number_verified,
                        };
                        dispatch(setUserDetail(refreshedAuth));
                        saveAuthToStorage(refreshedAuth);
                        dispatch(fetchCapabilitiesRequest());
                        dispatch(setAuthLoading(false));
                        return;
                    } catch (refreshError) {
                        console.error('Failed to refresh expired cached token:', refreshError);
                        dispatch(clearUserDetail());
                        clearAuthFromStorage();
                        clearCapabilitiesFromStorage();
                        dispatch(setAuthLoading(false));
                        return;
                    }
                }
                
                dispatch(setUserDetail(normalizedCachedAuth));
                dispatch(fetchCapabilitiesRequest());
                dispatch(setAuthLoading(false));
                return;
            }

            await getCurrentUser();
            const { tokens } = await fetchAuthSession();

            const companyId = tokens?.idToken?.payload?.['custom:companyId'];

            if (!companyId) {

                const userDetail = {
                    tokens: {
                        accessToken: tokens?.accessToken?.toString(),
                        idToken: tokens?.idToken?.toString()
                    }
                };
                dispatch(setUserDetail(userDetail));
                saveAuthToStorage(userDetail);

                try {
                    const setupResult = await dispatchAccountSetupAndWait(
                        dispatch,
                        store.getState
                    );

                    if (setupResult?.companyId) {
                        await pollTokenRefresh(20, 2000);
                        await updateUserInRedux(dispatch, true);
                        return;
                    }
                } catch (setupError) {
                    console.error('Account setup failed:', setupError);
                }
            }

            await updateUserInRedux(dispatch);
        } catch (error) {
            dispatch(clearUserDetail());
            clearAuthFromStorage();
            clearCapabilitiesFromStorage();
            dispatch(setAuthLoading(false));
        }
    };

    const handleBusinessDetailsClose = () => {
        setDismissedBusinessDialog(true);
    };

    const handleBusinessDetailsComplete = () => {
        setDismissedBusinessDialog(true);
    };

    useLayoutEffect(() => {
        const { pathname, search, hash } = window.location;
        if (pathname.length > 1 && pathname.endsWith('/')) {
            const normalizedPath = pathname.replace(/\/+$/, '');
            window.history.replaceState(null, '', `${normalizedPath}${search}${hash}`);
        }

        if (!isUserAuthenticated) {
            checkAuthSession();
        }
        // eslint-disable-next-line
    }, []);

    const isAuthLoading = useSelector((state) => state.auth.isAuthLoading);
    if (isAuthLoading) {
        return <AuthGateSkeleton />;
    }

    if (shouldPromptForBusinessDetails) {
        return (
            <BrowserRouter>
                <div
                    style={{
                        minHeight: '100dvh',
                        width: '100vw',
                        overflow: 'hidden',
                        position: 'relative',
                        background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
                    }}
                >
                    <OptionalBusinessDetailsDialog
                        open={true}
                        initialPhoneNumber={authUser?.phoneNumber ?? ''}
                        onClose={handleBusinessDetailsClose}
                        onComplete={handleBusinessDetailsComplete}
                    />
                </div>
            </BrowserRouter>
        );
    }

    return (
        <BrowserRouter>
            {isUserAuthenticated ? <MenuLayout /> : <RoutesBeforeLogin />}
        </BrowserRouter>
    );
};

export default Routes;
